// src/app/features/offers-policies/store/offers-policies.state.ts
//
// NgRx state for the /offers-policies feature (ShendetiOferta migration).
// Mirrors the cache/AsyncSlice conventions from `claims.state.ts`.
//
// Rule: no inFlight inside the state shape — reducers run before effects,
// so a store-based inFlight check would race with itself. Effects own a
// local `_inFlight: Set<string>` instead.

import {
  // Shared
  PagedResult,
  // Policies
  PolicySearchResultItem,
  PolicySummaryResponse,
  PolicyViewResponse,
  PolicyPaymentSummary,
  PolicyClaimItem,
  // Installments
  InstallmentTableItem,
  // Invoices
  InvoiceSearchRequest,
  InvoiceSearchResultItem,
  InvoiceFullResponse,
} from '../models/offers-policies.model';

// ── Cache entry ─────────────────────────────────────────────────────────────
export interface CacheEntry<T> {
  data: T;
  loadedAt: number;   // Date.now()
  ttlMs: number;
}

export function isCacheValid<T>(entry: CacheEntry<T> | null | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.loadedAt < entry.ttlMs;
}

// ── TTL constants (ms) ──────────────────────────────────────────────────────
export const TTL = {
  PER_INVOICE: 10 * 60 * 1000, // 10 min — invoice detail
  PER_POLICY:  10 * 60 * 1000, // 10 min — policy detail, cards, installments, perms
  SEARCH:       2 * 60 * 1000, // 2 min — policy / invoice search
} as const;

// ── Async slice ─────────────────────────────────────────────────────────────
export interface AsyncSlice<T> {
  data: T | null;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  cache: CacheEntry<T> | null;
  /** HTTP status of the last failure, when the slice records it (invoice detail). */
  errorStatus?: number | null;
}

export function emptySlice<T>(): AsyncSlice<T> {
  return { data: null, loading: false, loaded: false, error: null, cache: null };
}

// ── Sub-state shapes ────────────────────────────────────────────────────────

export interface PoliciesSubState {
  /** Latest policy search result (paged). */
  search:           AsyncSlice<PagedResult<PolicySearchResultItem>>;
  /** keyed by policyId — comprehensive policy view (50+ fields). */
  details:          Record<number, AsyncSlice<PolicyViewResponse>>;
  /** keyed by policyId — separate summary endpoint (used on detail page). */
  summaries:        Record<number, AsyncSlice<PolicySummaryResponse>>;
  /** keyed by policyId — payment summary (installments + payments). */
  payments:         Record<number, AsyncSlice<PolicyPaymentSummary>>;
  /** keyed by policyId */
  claims:           Record<number, AsyncSlice<PolicyClaimItem[]>>;
}

export interface InstallmentsSubState {
  /** keyed by policyId */
  byPolicy: Record<number, AsyncSlice<InstallmentTableItem[]>>;
}

export interface InvoicesSubState {
  /** Latest invoice search result (paged). */
  search:  AsyncSlice<PagedResult<InvoiceSearchResultItem>>;
  /** The request that produced `search` — null until a search runs / after Clear. */
  searchRequest: InvoiceSearchRequest | null;
  /** keyed by invoiceId */
  details: Record<number, AsyncSlice<InvoiceFullResponse>>;
}

// ── Feature state ───────────────────────────────────────────────────────────

export interface OffersPoliciesState {
  policies:     PoliciesSubState;
  installments: InstallmentsSubState;
  invoices:     InvoicesSubState;
}

// ── Initial state ───────────────────────────────────────────────────────────

export const initialPoliciesSubState: PoliciesSubState = {
  search:           emptySlice(),
  details:          {},
  summaries:        {},
  payments:         {},
  claims:           {},
};

export const initialInstallmentsSubState: InstallmentsSubState = {
  byPolicy: {},
};

export const initialInvoicesSubState: InvoicesSubState = {
  search:  emptySlice(),
  searchRequest: null,
  details: {},
};

export const initialOffersPoliciesState: OffersPoliciesState = {
  policies:     initialPoliciesSubState,
  installments: initialInstallmentsSubState,
  invoices:     initialInvoicesSubState,
};
